<template>
  <component
    :is="baseTag"
    @click="onClick"
    :type="buttonType"
    :to="to"
    :href="href"
    :class="[buttonClass, 'systemBtn', iconOnly ? 'systemBtn--icon-only' : null]"
    :data-method="method"
    :tabindex="$attrs.tabindex || 0"
    :target="target ? target : null"
  >
    <span class="systemBtn__text">
      <span v-if="faIcon" :class="iconClass" :style="{ color: iconColor }" />
      <span><slot>{{placeholderText}}</slot></span>
    </span>
  </component>
</template>

<script>
const styleType = [
  'primary',
  'secondary',
  'clear',
];

const COLORS = [
  'sapphire',
  'light-sapphire',
  'coral',
  'gray',
  'honey',
  'rust',
  'tansy',
];

export default {
  name: 'system-button',
  props: {
    href: {
      type: String,
      default: undefined,
    },
    to: {
      type: [String, Object],
      default: undefined,
    },
    type: {
      type: String,
      default: 'button',
      validator(value) {
        return ['button', 'submit'].includes(value);
      },
    },
    faIcon: {
      type: String,
      default: undefined,
    },
    styleType: {
      type: String,
      default: 'primary',
      validator(value) {
        return styleType.includes(value);
      },
    },
    color: {
      type: String,
      default: 'sapphire',
      validator(value) {
        return COLORS.includes(value);
      },
    },
    fullWidth: {
      type: Boolean,
    },
    fullIcon: {
      type: Boolean,
    },
    iconOnly: {
      type: Boolean,
      default: false,
    },
    iconColor: {
      type: [String, undefined],
      default: undefined,
      validator(value) {
        return value === undefined || /^#[a-fA-F0-9]{3,6}$/.test(value);
      },
    },
    small: {
      type: Boolean,
    },
    inline: {
      type: Boolean,
    },
    method: {
      type: String,
      default: undefined,
    },
    target: {
      type: String,
      default: undefined,
      validator(value) {
        return value === undefined || '_blank';
      },
    },
  },
  computed: {
    isButton() {
      return !this.href && !this.to;
    },
    baseTag() {
      if (this.isButton) {
        return 'button';
      }
      return this.href ? 'a' : 'router-link';
    },
    buttonType() {
      return this.isButton ? this.type : undefined;
    },
    buttonClass() {
      return [{
        small: this.small,
        fullWidth: this.fullWidth,
        inline: this.inline,
      }, `${this.styleType}`,
      `${this.styleType}--${this.color}`];
    },
    iconClass() {
      const prefix = this.fullIcon ? 'fa' : 'fal';
      return `${prefix} systemBtn__icon fa-${this.faIcon}`;
    },
    placeholderText() {
      return this.faIcon ? '' : 'Click me!';
    },
  },
  methods: {
    onClick() {
      this.$emit('click');
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.$emit('ready');
    });
  },
};
</script>

<style lang="scss" scoped>
.systemBtn {
  --button-fade: var(--transition-fade);
  --button-radius: calc(var(--grid-unit) * 4);

  position: relative;
  display: inline-block;
  padding: var(--grid-unit) calc(var(--grid-unit) * 2);
  font-family: var(--sans-serif);
  font-size: 16px;
  font-weight: 600;
  font-variant-numeric: lining-nums;
  color: var(--button-text-color);
  text-align: center;
  cursor: pointer;
  background-color: var(--button-bg-color);
  border: 0 solid transparent;
  border-radius: var(--button-radius);
  outline: none;
  transition:
    color var(--button-fade),
    background-color var(--button-fade);

  &__text {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: none;
    white-space: nowrap;
    padding-left: 4px;
    padding-right: 4px;

    * > span {
      display: inline-block;
    }
  }

  &:hover {
    text-decoration: none;
  }

  &:focus {
    box-shadow: 0 0 0 3px var(--input-focus-color);
  }

  &:disabled,
  &[disabled] {
    color: var(--button-text-color-disabled);
    cursor: no-drop;
    background-color: var(--button-bg-color-disabled);
  }

  &:not([disabled]),
  &:not(:disabled) {
    &:hover,
    &:focus {
      color: var(--button-text-color-hover);
      background-color: var(--button-bg-color-hover);
    }

    &:active {
      color: var(--button-text-color-active);
      background-color: var(--button-bg-color-active);
    }
  }

  &__icon {
    margin-right: var(--grid-unit);
    font-size: var(--font-icon-medium);
    color: inherit;
    transition: color var(--button-fade);
  }

  &--icon-only {
    .systemBtn__icon {
      margin-right: 0;
    }
  }

  &.fullWidth {
    width: 100%;
  }

  &.small {
    padding: calc(var(--grid-unit) / 4) var(--grid-unit);

    .systemBtn__icon {
      margin-right: calc(var(--grid-unit) / 2);
      font-size: var(--font-icon-small);
    }
  }

  &.primary {
    --button-text-color-active: var(--button-text-color);
    --button-text-color-hover: var(--button-text-color);

    &--sapphire {
      --button-text-color: var(--gray-0);
      --button-text-color-disabled: var(--sapphire-50);
      --button-bg-color: var(--sapphire-500);
      --button-bg-color-disabled: var(--gray-400);
      --button-bg-color-hover: var(--sapphire-900);
      --button-bg-color-active: var(--sapphire-300);
    }

    &--light-sapphire {
      --button-text-color: var(--sapphire-300);
      --button-text-color-hover: var(--sapphire-500);
      --button-text-color-active: var(--sapphire-700);
      --button-text-color-disabled: var(--sapphire-50);
      --button-bg-color: var(--sapphire-50);
      --button-bg-color-disabled: var(--sapphire-50);
      --button-bg-color-hover: var(--sapphire-50);
      --button-bg-color-active: var(--sapphire-100);
    }

    &--coral {
      --button-text-color: var(--coral-80);
      --button-text-color-disabled: var(--coral-0);
      --button-bg-color: var(--coral-20);
      --button-bg-color-disabled: var(--gray-400);
      --button-bg-color-hover: var(--coral-30);
      --button-bg-color-active: var(--coral-40);
    }

    &--gray {
      --button-text-color: var(--gray-0);
      --button-text-color-disabled: var(--gray-400);
      --button-bg-color: var(--gray-500);
      --button-bg-color-disabled: var(--gray-300);
      --button-bg-color-hover: var(--gray-500);
      --button-bg-color-active: var(--gray-almostBlack);
    }

    &--honey {
      --button-text-color: var(--gray-almostBlack);
      --button-text-color-disabled: var(--gray-400);
      --button-bg-color: var(--honey-300);
      --button-bg-color-disabled: var(--honey-50);
      --button-bg-color-hover: var(--honey-400);
      --button-bg-color-active: var(--honey-500);
    }

    &--rust {
      --button-text-color: var(--gray-0);
      --button-text-color-disabled: var(--rust-0);
      --button-bg-color: var(--rust-50);
      --button-bg-color-disabled: #E8B6A1;
      --button-bg-color-hover: var(--rust-60);
      --button-bg-color-active: var(--rust-50);
    }

    &--tansy {
      --button-text-color: var(--gray-0);
      --button-text-color-disabled: var(--tansy-0);
      --button-bg-color: var(--tansy-50);
      --button-bg-color-disabled: #A3D4E8;
      --button-bg-color-hover: var(--tansy-60);
      --button-bg-color-active: var(--tansy-70);
    }
  }

  &.secondary {
    &--sapphire {
      --button-text-color: var(--sapphire-900);
      --button-text-color-disabled: var(--gray-500);
      --button-bg-color: var(--sapphire-50);
      --button-bg-color-disabled: var(--gray-300);
      --button-bg-color-hover: var(--sapphire-100);
      --button-bg-color-active: var(--sapphire-300);
    }

    &--coral {
      --button-text-color: var(--coral-80);
      --button-text-color-disabled: #C7A6A1;
      --button-bg-color: var(--coral-5);
      --button-bg-color-disabled: #FDEFEF;
      --button-bg-color-hover: var(--coral-10);
      --button-bg-color-active: var(--coral-15);
    }

    &--gray {
      --button-text-color: var(--gray-almostBlack);
      --button-text-color-disabled: var(--gray-300);
      --button-bg-color: var(--gray-5);
      --button-bg-color-disabled: var(--gray-5);
      --button-bg-color-hover: var(--gray-300);
      --button-bg-color-active: var(--gray-300);
    }

    &--honey {
      --button-text-color: var(--honey-700);
      --button-text-color-disabled: #C6B39D;
      --button-bg-color: var(--honey-50);
      --button-bg-color-disabled: #FFF9E9;
      --button-bg-color-hover: var(--honey-50);
      --button-bg-color-active: var(--honey-300);
    }

    &--rust {
      --button-text-color: var(--rust-80);
      --button-text-color-disabled: #C7A799;
      --button-bg-color: var(--rust-5);
      --button-bg-color-disabled: #FFF2ED;
      --button-bg-color-hover: var(--rust-10);
      --button-bg-color-active: var(--rust-15);
    }

    &--tansy {
      --button-text-color: var(--tansy-80);
      --button-text-color-disabled: #A3C7D4;
      --button-bg-color: var(--tansy-5);
      --button-bg-color-disabled: #F0F7FB;
      --button-bg-color-hover: var(--tansy-10);
      --button-bg-color-active: var(--tansy-15);
    }
  }

  &.clear {
    --button-bg-color: transparent;
    --button-bg-color-disabled: transparent;
    --button-bg-color-hovered: transparent;
    --button-bg-color-active: transparent;

    &--sapphire {
      --button-text-color: var(--sapphire-500);
      --button-text-color-disabled: var(--gray-300);
      --button-text-hovered: var(--sapphire-700);
      --button-text-active: var(--sapphire-700);
    }

    &--coral {
      --button-text-color: var(--coral-50);
      --button-text-color-disabled: var(--gray-400);
      --button-text-hovered: var(--coral-60);
      --button-text-active: var(--coral-70);
    }

    &--gray {
      --button-text-color: var(--gray-500);
      --button-text-color-disabled: var(--gray-300);
      --button-text-hovered: var(--gray-500);
      --button-text-active: var(--gray-almostBlack);
    }

    &--honey {
      --button-text-color: var(--honey-500);
      --button-text-color-disabled: var(--gray-400);
      --button-text-hovered: var(--honey-600);
      --button-text-active: var(--honey-700);
    }

    &--rust {
      --button-text-color: var(--rust-50);
      --button-text-color-disabled: var(--rust-15);
      --button-text-hovered: var(--rust-60);
      --button-text-active: var(--rust-70);
    }

    &--tansy {
      --button-text-color: var(--tansy-50);
      --button-text-color-disabled: var(--tansy-20);
      --button-text-hovered: var(--tansy-60);
      --button-text-active: var(--tansy-70);
    }
  }

  &.inline {
    display: inline-block;
    text-align: inherit;
    letter-spacing: inherit;

    &.clear {
      padding: 0;
      border: none;

      & .systemBtn__text {
        font: inherit;
        letter-spacing: inherit;
        white-space: inherit;
      }

      & .systemBtn__text > span:last-child {
        text-decoration: underline;
      }
    }
  }
}
</style>
